import { useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";

import { PageHeader } from "~/components/page-header";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { errorMessage, isConflict, type ApiClient, type ListPayload } from "~/lib/api";
import { useApi } from "~/lib/use-api";
import { useCreateStorefront, type StorefrontListItem } from "~/lib/queries";

export const Route = createFileRoute("/_authed/storefronts/new")({
  component: NewStorefrontPage,
});

/**
 * The thin provisioning slice: onboarding a merchant stops being a psql insert.
 *
 * Two fields, and deliberately only two. Callbacks, API keys and Shopify
 * credentials all have their own screens in M2 and M3; asking for them here
 * would make the common case -- "spin up a storefront so I can start testing" --
 * the slowest one.
 *
 * Tier-1 write: it creates a record and destroys nothing, so there is no typed
 * confirmation. The audit row is written server-side in the same transaction as
 * the insert, so neither can commit without the other.
 */
function NewStorefrontPage() {
  const navigate = useNavigate();
  const api = useApi();
  const create = useCreateStorefront();

  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [nameError, setNameError] = useState<string>();
  const [domainError, setDomainError] = useState<ConflictMessage | string>();

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setNameError(undefined);
    setDomainError(undefined);

    const trimmedName = name.trim();
    const trimmedDomain = domain.trim();

    if (trimmedName === "") {
      setNameError("A storefront needs a name.");
      return;
    }
    // Loose on purpose: the server validates hostname_rfc1123 and is the
    // authority. This only catches the shapes that are obviously not a domain,
    // so the round trip is spent on real answers.
    if (trimmedDomain !== "" && !/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(trimmedDomain)) {
      setDomainError("That doesn't look like a domain. Use the bare host, e.g. acme.myshopify.com.");
      return;
    }

    try {
      const created = await create.mutateAsync({
        storefront_name: trimmedName,
        // The server lowercases and trims, and reads an empty string as "none".
        shop_domain: trimmedDomain === "" ? undefined : trimmedDomain,
      });
      void navigate({
        to: "/storefronts/$storefrontId",
        params: { storefrontId: created.storefront_id },
        search: { created: true },
      });
    } catch (error) {
      if (isConflict(error)) {
        // The realistic failure is re-adding a merchant that already exists, and
        // the useful answer is where they are -- not a toast saying no.
        setDomainError(await describeConflict(api, trimmedDomain, errorMessage(error)));
        return;
      }
      setNameError(errorMessage(error));
    }
  }

  return (
    <div className="mx-auto max-w-xl space-y-5">
      <div>
        <Button variant="ghost" size="sm" render={<Link to="/storefronts" />}>
          <ArrowLeftIcon className="size-3.5" />
          All storefronts
        </Button>
      </div>

      <PageHeader
        title="New storefront"
        description="A name is enough to start. Everything else has its own screen."
      />

      <form onSubmit={(event) => void submit(event)}>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Details</CardTitle>
            <CardDescription>
              Callbacks, API keys and Shopify credentials are configured after the storefront
              exists.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="storefront_name">Name</Label>
              <Input
                id="storefront_name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Acme Store"
                aria-invalid={nameError ? true : undefined}
                aria-describedby={nameError ? "storefront_name_error" : undefined}
                autoFocus
              />
              {nameError ? (
                <p id="storefront_name_error" className="text-sm text-destructive">
                  {nameError}
                </p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="shop_domain">
                Shop domain <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="shop_domain"
                value={domain}
                onChange={(event) => setDomain(event.target.value)}
                placeholder="acme.myshopify.com"
                aria-invalid={domainError ? true : undefined}
                aria-describedby={domainError ? "shop_domain_error" : undefined}
              />
              {domainError ? (
                <p id="shop_domain_error" className="text-sm text-destructive">
                  {typeof domainError === "string" ? (
                    domainError
                  ) : (
                    <>
                      {domainError.text}{" "}
                      {domainError.storefront ? (
                        <Link
                          to="/storefronts/$storefrontId"
                          params={{ storefrontId: domainError.storefront.id }}
                          className="inline-flex items-center gap-1 underline underline-offset-4"
                        >
                          {domainError.storefront.name}
                          <ArrowRightIcon className="size-3" />
                        </Link>
                      ) : null}
                    </>
                  )}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Must be unique across every storefront.
                </p>
              )}
            </div>
          </CardContent>

          <CardFooter className="gap-2">
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Creating…" : "Create storefront"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={create.isPending}
              render={<Link to="/storefronts" />}
            >
              Cancel
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
}

interface ConflictMessage {
  text: string;
  storefront?: { id: string; name: string };
}

/**
 * Turns "that domain is taken" into "it belongs to Acme Store, here it is".
 *
 * The 409 names the domain but not its owner, so this looks the owner up. The
 * lookup is best-effort: if it fails or finds nothing -- a race, or a domain
 * that no longer matches the search -- the server's own message stands rather
 * than the error becoming a second error.
 */
async function describeConflict(
  api: ApiClient,
  domain: string,
  fallback: string,
): Promise<ConflictMessage> {
  try {
    const found = await api.get<ListPayload<StorefrontListItem>>(
      `/storefronts?search=${encodeURIComponent(domain)}&limit=2`,
    );
    const owner = found.items.find(
      (item) => item.shop_domain?.toLowerCase() === domain.toLowerCase(),
    );
    if (owner) {
      return {
        text: "That domain already belongs to",
        storefront: { id: owner.storefront_id, name: owner.storefront_name },
      };
    }
  } catch {
    // Fall through to the server's message.
  }
  return { text: fallback };
}
